import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Minus, Search, LayoutGrid, Rows3, Upload, Link2, ImageOff } from 'lucide-react';
import api, { apiError, ASAL_API as API_ASAL } from '../lib/api';
import { formatIDR, SIZES } from '../lib/format';
import { MoneyInput } from '../components/MoneyInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { HapusCari } from '../components/HapusCari';
import { useAuth } from '../context/AuthContext';

const CATEGORIES = ['Kebaya', 'Baju Bodo', 'Bawahan', 'Dress', 'Aksesoris', 'Jas Tutup', 'Kemeja / Batik Pria', 'Seserahan', 'Ring Box'];


// Alamat gambar untuk satu produk: foto unggahan menang atas link.
const fotoSrc = (p) => {
  if (!p) return '';
  if (p.has_photo) return `${API_ASAL}/api/products/${p.id}/photo?v=${encodeURIComponent(p.photo_updated || '')}`;
  return p.photo || '';
};

// Link berbagi Google Drive itu halaman web, bukan gambar. Diubah ke bentuk
// yang mengembalikan berkas gambarnya. Backend melakukan hal yang sama.
const DRIVE_POLA = [/\/file\/d\/([A-Za-z0-9_-]{10,})/, /[?&]id=([A-Za-z0-9_-]{10,})/, /\/d\/([A-Za-z0-9_-]{10,})/];
const rapikanFotoUrl = (u) => {
  let s = (u || '').trim();
  while (s.length && (s[0] === '"' || s[0] === "'")) s = s.slice(1);
  while (s.length && (s[s.length - 1] === '"' || s[s.length - 1] === "'")) s = s.slice(0, -1);
  if (!s) return '';
  const low = s.toLowerCase();
  if (low.includes('drive.google.com') || low.includes('docs.google.com')) {
    for (const pola of DRIVE_POLA) {
      const m = s.match(pola);
      if (m) return `https://lh3.googleusercontent.com/d/${m[1]}`;
    }
  }
  return s;
};

// Berkas dikirim apa adanya. Dipakai untuk format yang browser tidak bisa baca
// (HEIC dari iPhone) dan sebagai jalan keluar kalau pengecilan di browser gagal.
const bacaMentah = (file) =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const hasil = String(fr.result || '');
      const potong = hasil.indexOf(',');
      if (potong < 0) { reject(new Error('kosong')); return; }
      resolve(hasil.slice(potong + 1));
    };
    fr.onerror = () => reject(fr.error || new Error('gagal baca'));
    fr.readAsDataURL(file);
  });

const HEIC_POLA = /\.(heic|heif|avif)$/i;
const mungkinHeic = (file) =>
  HEIC_POLA.test(file.name || '') || /heic|heif|avif/i.test(file.type || '');

// Dikecilkan dulu di browser supaya database tidak membengkak.
const kecilkanFoto = (file, sisiMaks = 1200, mutu = 0.75) =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const s = Math.min(1, sisiMaks / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * s);
      c.height = Math.round(img.height * s);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL('image/jpeg', mutu).split(',')[1]);
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });

const EMPTY = {
  code: '', name: '', category: 'Kebaya', rental_price: '', sizes: {},
  default_bawahan_id: null, set_contents: '', photo: '', notes: '', purchase_price: '',
};

export default function Collection() {
  const { user } = useAuth();
  const isOwner = user?.role !== 'karyawan';   // harga beli (modal) hanya untuk owner
  const [products, setProducts] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [view, setView] = useState('list');
  const [fotoBaru, setFotoBaru] = useState(null);   // base64 menunggu disimpan
  const [fotoMime, setFotoMime] = useState('image/jpeg');
  const [fotoMentah, setFotoMentah] = useState(false); // true = berkas asli, browser tidak bisa pratinjau
  const [modeFoto, setModeFoto] = useState('unggah');
  const berkasRef = useRef(null);

  const load = () => api.get('/products').then((r) => setProducts(r.data));
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () =>
      (products || []).filter(
        (p) =>
          (!catFilter || p.category === catFilter) &&
          (p.name.toLowerCase().includes(q.toLowerCase()) || p.code.toLowerCase().includes(q.toLowerCase()))
      ),
    [products, catFilter, q]
  );

  const bawahanOptions = (products || []).filter((p) => p.category === 'Bawahan');

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      code: p.code, name: p.name, category: p.category, rental_price: String(p.rental_price),
      sizes: { ...(p.sizes || {}) }, default_bawahan_id: p.default_bawahan_id || null,
      set_contents: p.set_contents || '', photo: p.photo || '', notes: p.notes || '',
      has_photo: !!p.has_photo, photo_updated: p.photo_updated || '',
      purchase_price: p.purchase_price ? String(p.purchase_price) : '',
    });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSize = (s, v) => setForm((f) => ({ ...f, sizes: { ...f.sizes, [s]: Math.max(0, v) } }));

  const submit = async () => {
    if (!form.code.trim() || !form.name.trim()) return toast.error('Kode dan nama produk wajib diisi');
    setSaving(true);
    try {
      const { has_photo: _hp, photo_updated: _pu, purchase_price: modal, ...bersih } = form;
      const payload = {
        ...bersih,
        photo: rapikanFotoUrl(form.photo),
        rental_price: Number(form.rental_price) || 0,
        sizes: Object.fromEntries(Object.entries(form.sizes).filter(([, v]) => v > 0)),
        sizes_damaged: editing ? editing.sizes_damaged || {} : {},
      };
      // Karyawan tidak mengirim harga beli sama sekali (server juga mengabaikannya).
      if (isOwner) payload.purchase_price = Number(modal) || 0;
      let id = editing?.id;
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
      } else {
        const r = await api.post('/products', payload);
        id = r.data?.id;
      }
      // Foto diunggah SESUDAH produk punya id.
      if (fotoBaru && id) {
        await api.post(`/products/${id}/photo`, { image_base64: fotoBaru, mime_type: fotoMime });
      }
      toast.success(editing ? 'Produk diperbarui' : 'Produk ditambahkan');
      setFotoBaru(null);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (open) { setFotoBaru(null); setFotoMime('image/jpeg'); setFotoMentah(false); setModeFoto('unggah'); }
  }, [open]);

  const remove = async (p) => {
    try {
      await api.delete(`/products/${p.id}`);
      toast.success('Produk dihapus');
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const field = (testid, label, key, props = {}) => (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-mocha">{label}</label>
      <input
        data-testid={testid}
        value={form[key] ?? ''}
        onChange={(e) => set(key, e.target.value)}
        className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30"
        {...props}
      />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="collection-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-ink tracking-tight">Koleksi</h1>
          {products && <p className="text-xs text-mocha mt-1">{filtered.length} dari {products.length} produk</p>}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5 bg-white border border-border rounded-xl p-1" data-testid="collection-view-toggle">
            <button
              data-testid="collection-view-list"
              onClick={() => setView('list')}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${view === 'list' ? 'bg-brown text-white' : 'text-mocha hover:bg-ivory'}`}
            >
              <Rows3 size={14} /> List
            </button>
            <button
              data-testid="collection-view-grid"
              onClick={() => setView('grid')}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${view === 'grid' ? 'bg-brown text-white' : 'text-mocha hover:bg-ivory'}`}
            >
              <LayoutGrid size={14} /> Gambar
            </button>
          </div>
          <button
            data-testid="add-item-button"
            onClick={openAdd}
            className="inline-flex items-center gap-2 rounded-xl bg-brown text-white px-5 py-2.5 text-sm font-semibold hover:bg-brown-med transition-colors"
          >
            <Plus size={16} /> Tambah Produk
          </button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-mocha" />
        <input
          data-testid="collection-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cari kode atau nama produk…"
          className="w-full rounded-xl border border-border bg-white pl-10 pr-9 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30"
        />
        <HapusCari show={!!q} onClear={() => setQ('')} testid="collection-search-clear" />
      </div>
      <div className="flex gap-1.5 flex-wrap" data-testid="collection-category-filters">
        {['', ...CATEGORIES].map((c) => (
          <button
            key={c || 'all'}
            data-testid={`collection-filter-${(c || 'semua').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            onClick={() => setCatFilter(c)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
              catFilter === c ? 'bg-brown text-white' : 'bg-white border border-border text-mocha hover:bg-ivory'
            }`}
          >
            {c || 'Semua'}
          </button>
        ))}
      </div>

      {!products ? (
        <p className="text-sm text-mocha py-10 text-center">Memuat koleksi…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-mocha py-10 text-center" data-testid="collection-empty">Tidak ada produk yang cocok.</p>
      ) : view === 'list' ? (
        <div className="space-y-2" data-testid="collection-list">
          {filtered.map((p) => (
            <div key={p.id} data-testid={`collection-row-${p.code}`} className="bg-white rounded-xl border border-border shadow-soft px-4 py-3 flex flex-wrap items-center gap-3">
              <span className="font-display font-bold text-brown text-sm w-16">{p.code}</span>
              <span className="text-sm font-semibold text-ink flex-1 min-w-[160px] truncate" title={p.name}>{p.name}</span>
              <span className="text-xs text-mocha">{p.category}</span>
              <span className="text-xs font-semibold text-brown-dark">{formatIDR(p.rental_price)}/pcs</span>
              <span className="text-xs text-mocha">stok {p.total_stock}</span>
              <div className="flex gap-1.5">
                <button
                  data-testid={`edit-item-${p.code}`}
                  onClick={() => openEdit(p)}
                  className="inline-flex items-center gap-1 rounded-lg border border-brown/30 text-brown px-2.5 py-1.5 text-xs font-bold hover:bg-brown/5"
                >
                  <Pencil size={12} /> Edit
                </button>
                <button
                  data-testid={`delete-item-${p.code}`}
                  onClick={() => remove(p)}
                  className="inline-flex items-center justify-center rounded-lg border border-danger/30 text-danger px-2.5 py-1.5 hover:bg-danger/5"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {filtered.map((p) => (
            <div key={p.id} data-testid={`collection-card-${p.code}`} className="bg-white rounded-2xl border border-border shadow-soft overflow-hidden card-lift">
              <div className="aspect-[4/3] bg-muted">
                {fotoSrc(p) && <img src={fotoSrc(p)} alt={p.name} className="w-full h-full object-cover" loading="lazy" />}
              </div>
              <div className="p-3.5">
                <div className="flex items-center justify-between">
                  <span className="font-display font-bold text-brown text-sm">{p.code}</span>
                  <span className="text-[10px] text-mocha">stok {p.total_stock}</span>
                </div>
                <p className="text-sm font-semibold text-ink mt-0.5 truncate" title={p.name}>{p.name}</p>
                <p className="text-xs text-mocha truncate">{p.category}</p>
                <p className="font-display text-brown-dark mt-1">{formatIDR(p.rental_price)}<span className="text-[10px] text-mocha font-sans">/pcs</span></p>
                <div className="flex gap-2 mt-3">
                  <button
                    data-testid={`edit-item-${p.code}`}
                    onClick={() => openEdit(p)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-brown/30 text-brown py-1.5 text-xs font-bold hover:bg-brown/5 transition-colors"
                  >
                    <Pencil size={12} /> Edit
                  </button>
                  <button
                    data-testid={`delete-item-${p.code}`}
                    onClick={() => remove(p)}
                    className="inline-flex items-center justify-center rounded-xl border border-danger/30 text-danger px-3 py-1.5 hover:bg-danger/5 transition-colors"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="item-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-brown-dark text-xl">{editing ? 'Edit Produk' : 'Tambah Produk'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {field('item-code-input', 'Kode', 'code', { placeholder: 'BM001', disabled: !!editing })}
            {field('item-name-input', 'Nama Produk', 'name', { placeholder: 'KEBAYA JANGGAN SAGE' })}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-mocha">Kategori</label>
              <select data-testid="item-category-select" value={form.category} onChange={(e) => set('category', e.target.value)} className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-mocha">Harga Sewa /pcs</label>
              <MoneyInput
                data-testid="item-price-input"
                value={form.rental_price}
                onChange={(v) => set('rental_price', v)}
                placeholder="100.000"
                className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30"
              />
            </div>
            {isOwner && (
              <div className="col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-mocha">Harga Beli /pcs (modal, hanya terlihat owner)</label>
                <MoneyInput
                  data-testid="item-purchase-input"
                  value={form.purchase_price}
                  onChange={(v) => set('purchase_price', v)}
                  placeholder="mis. 290.000 (yang dibayar untuk satu potong)"
                  className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30"
                />
              </div>
            )}
          </div>
          <div className="mt-3">
            <label className="text-xs font-semibold uppercase tracking-wider text-mocha">Bawahan Standar (opsional)</label>
            <select data-testid="item-bawahan-select" value={form.default_bawahan_id || ''} onChange={(e) => set('default_bawahan_id', e.target.value || null)} className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30">
              <option value="">— Tanpa bawahan —</option>
              {bawahanOptions.map((b) => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
            </select>
          </div>
          <div className="mt-3">
            <label className="text-xs font-semibold uppercase tracking-wider text-mocha">Stok per Ukuran</label>
            <div className="flex flex-wrap gap-2 mt-1.5" data-testid="item-sizes-editor">
              {SIZES.map((s) => {
                const val = form.sizes[s] || 0;
                return (
                  <div key={s} className={`rounded-xl border px-2.5 py-1.5 text-center ${val > 0 ? 'border-brown bg-brown/5' : 'border-border bg-white'}`}>
                    <p className="text-[9px] font-bold text-mocha whitespace-nowrap">{s}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <button type="button" data-testid={`item-size-${s.replace(/[^A-Z0-9]/gi, '')}-minus`} disabled={val <= 0} onClick={() => setSize(s, val - 1)} className="w-5 h-5 rounded border border-border bg-white text-brown flex items-center justify-center disabled:opacity-30">
                        <Minus size={10} />
                      </button>
                      <span className="w-5 text-center text-xs font-bold">{val}</span>
                      <button type="button" data-testid={`item-size-${s.replace(/[^A-Z0-9]/gi, '')}-plus`} onClick={() => setSize(s, val + 1)} className="w-5 h-5 rounded border border-border bg-white text-brown flex items-center justify-center">
                        <Plus size={10} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mt-3">{field('item-set-input', 'Isi Set', 'set_contents', { placeholder: 'Baju + Songket + Jilbab' })}</div>
          <div className="mt-3">
            <label className="text-xs font-semibold uppercase tracking-wider text-mocha">Foto Produk</label>

            <div className="mt-2 flex gap-2">
              {[
                { v: 'unggah', t: 'Dari komputer', ic: Upload },
                { v: 'link', t: 'Tempel link', ic: Link2 },
              ].map((o) => (
                <button key={o.v} type="button" data-testid={`foto-mode-${o.v}`}
                  onClick={() => setModeFoto(o.v)}
                  className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-bold transition ${
                    modeFoto === o.v ? 'border-brown bg-brown/8 text-brown-dark' : 'border-border bg-white text-mocha hover:bg-ivory'}`}>
                  <o.ic size={13} /> {o.t}
                </button>
              ))}
            </div>

            {modeFoto === 'unggah' ? (
              <div className="mt-2">
                <button type="button" data-testid="foto-pilih-berkas" onClick={() => berkasRef.current?.click()}
                  className="w-full rounded-xl border border-dashed border-brown/45 bg-white py-3 text-xs font-bold text-brown hover:bg-ivory">
                  {fotoBaru ? 'Ganti foto…' : 'Pilih foto dari komputer…'}
                </button>
                <input ref={berkasRef} type="file" className="hidden" data-testid="foto-berkas"
                  accept="image/*,.heic,.HEIC,.heif,.HEIF,.avif,.jpg,.jpeg,.png,.webp,.bmp,.tif,.tiff"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    try {
                      let b64;
                      let mime = f.type || 'image/jpeg';
                      let mentah = false;
                      if (mungkinHeic(f)) {
                        // Browser tidak bisa membaca HEIC. Kirim berkas aslinya,
                        // mesin aplikasi yang mengubahnya jadi JPG.
                        b64 = await bacaMentah(f);
                        mentah = true;
                      } else {
                        try {
                          b64 = await kecilkanFoto(f);
                          mime = 'image/jpeg';
                        } catch {
                          // Format apa pun yang gagal dibaca browser tetap dicoba di mesin aplikasi.
                          b64 = await bacaMentah(f);
                          mentah = true;
                        }
                      }
                      if (!b64) throw new Error('kosong');
                      setFotoBaru(b64); setFotoMime(mime); setFotoMentah(mentah);
                      set('photo', '');
                      const kb = Math.round((b64.length * 3) / 4 / 1024);
                      toast.success(mentah
                        ? `Berkas ${f.name} siap (${kb} KB). Diubah jadi JPG saat Simpan Produk.`
                        : `Foto siap (${kb} KB). Tekan Simpan Produk.`);
                    } catch {
                      toast.error('Berkas ini tidak bisa dibaca sebagai foto. Coba berkas lain.');
                    }
                    finally { if (berkasRef.current) berkasRef.current.value = ''; }
                  }} />
                <p className="text-[11px] text-mocha mt-1.5 leading-snug">
                  Bisa JPG, JPEG, PNG, WEBP, TIFF, dan HEIC dari iPhone. Foto diputar sesuai arah aslinya,
                  dikecilkan, lalu disimpan di aplikasi sebagai JPG.
                </p>
              </div>
            ) : (
              <div className="mt-2">
                <input data-testid="item-photo-input" value={form.photo ?? ''} placeholder="Tempel link foto atau link Google Drive"
                  onChange={(e) => { set('photo', e.target.value); setFotoBaru(null); }}
                  className="w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
                {form.photo && rapikanFotoUrl(form.photo) !== form.photo.trim() && (
                  <p className="text-[11px] text-gold-dark mt-1.5 leading-snug" data-testid="foto-link-rapi">
                    Link Google Drive dikenali. Yang dipakai: <b>{rapikanFotoUrl(form.photo)}</b>
                  </p>
                )}
                <p className="text-[11px] text-mocha mt-1.5 leading-snug">
                  Foto di Google Drive harus disetel <b>"Siapa saja yang memiliki link"</b>. Kalau masih terbatas, gambarnya tidak akan muncul.
                </p>
              </div>
            )}

            <div className="mt-3 rounded-xl border border-border bg-white p-2">
              <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold mb-2">Pratinjau</p>
              {(() => {
                const src = fotoBaru
                  ? `data:${fotoMime};base64,${fotoBaru}`
                  : (form.photo ? rapikanFotoUrl(form.photo) : fotoSrc({ ...form, id: editing?.id }));
                if (!src) {
                  return (
                    <div className="h-40 rounded-lg bg-ivory flex flex-col items-center justify-center gap-1.5 text-mocha">
                      <ImageOff size={20} />
                      <span className="text-[11px]">Belum ada foto</span>
                    </div>
                  );
                }
                return (
                  <>
                    <img src={src} alt="Pratinjau" data-testid="foto-pratinjau"
                      className="w-full h-48 object-contain rounded-lg bg-ivory"
                      onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }} />
                    <div style={{ display: 'none' }}
                      className={`h-40 rounded-lg flex-col items-center justify-center gap-1.5 px-4 text-center ${fotoMentah ? 'bg-gold/10 border border-gold/40 text-gold-dark' : 'bg-danger/8 border border-danger/30 text-danger'}`}>
                      <ImageOff size={20} />
                      {fotoMentah ? (
                        <>
                          <span className="text-[11px] font-bold">Pratinjau tidak tersedia untuk format ini</span>
                          <span className="text-[10px] text-mocha leading-snug">
                            Browser tidak bisa menampilkan HEIC. Berkasnya tetap tersimpan dan
                            diubah jadi JPG oleh aplikasi saat kamu tekan Simpan Produk.
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-[11px] font-bold">Gambar tidak bisa ditampilkan</span>
                          <span className="text-[10px] text-mocha">Cek izin berbagi link-nya, atau unggah dari komputer</span>
                        </>
                      )}
                    </div>
                    {(fotoBaru || form.photo || form.has_photo) && (
                      <button type="button" data-testid="foto-hapus"
                        onClick={async () => {
                          setFotoBaru(null); setFotoMentah(false); setFotoMime('image/jpeg'); set('photo', '');
                          if (editing?.id && form.has_photo) {
                            try { await api.delete(`/products/${editing.id}/photo`); } catch { /* diamkan */ }
                            set('has_photo', false);
                          }
                        }}
                        className="mt-2 w-full rounded-lg border border-border py-2 text-[11px] font-bold text-mocha hover:bg-ivory">
                        Hapus foto
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          <div className="mt-3">{field('item-notes-input', 'Catatan', 'notes')}</div>
          <button data-testid="save-item-button" onClick={submit} disabled={saving} className="mt-4 w-full rounded-xl bg-brown text-white py-3 font-semibold text-sm hover:bg-brown-med transition-colors disabled:opacity-60">
            {saving ? 'Menyimpan…' : 'Simpan Produk'}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
